import React, { useRef, useState } from 'react';
import { styled } from 'styled-components';
import { TextField, Button, Typography, CircularProgress, SvgIcon, Box, Link } from '@mui/material';
import { PhoneInput } from './PhoneInput';
import '../../styles/Login.css';
import NextIcon from '@src/assets/onboarding-screen/Frame.svg';

//Images
import Logo from '../../assets/Logo/ylPartsLogo.png';
import ErrorIcon from '../../assets/icons/Danger.png';
import ArrowLeft from '../../assets/icons/arrow-left.svg';
import { IsMobileWidth } from '../MediaQueryUtils';
import CustomButton from '../CustomButton/CustomButton';

const Container = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	width: 100%;
	height: 100%;
`;

const FormContainer = styled.form`
	width: 100%;
`;

const LogoContainer = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	justify-content: center;
	align-items: center;
	margin-bottom: 40px;
`;

const LogoImage = styled.img`
	height: 70px;
	// width: 230px;
`;

const LoginInfoContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	width: 337px;
	margin: 0 auto;
`;

const Heading = styled(Typography)`
	text-transform: capitalize;
	color: var(--default-text-color);
	text-align: center;
`;

const Paragraph = styled(Typography)`
	font-size: 16px;
	color: var(--lighter-text-color) !important;
	text-align: center;
	padding-left: 3px;
	padding-right: 3px;
`;

const FieldsContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	flex-direction: column;
	margin-top: 20px;
	gap: 18px;
	padding: 10px;
	width: 100%;
`;

const StyledButton = styled(Button)`
	width: 326px;
`;

const StyledError = styled.div`
	display: flex;
	justify-content: space-around;
	align-items: center;
	gap: 5px;
	color: #f26184;
	margin-bottom: 15px;
`;

const StyledTextFieldContainer = styled(TextField)`
	input {
		text-align: center;
		font-size: 1.5rem;
		width: 70px;
		height: 48px;
		border-radius: 4px;
		border: 1.5px solid ${(props) => (props.error ? 'red' : '#e9e9e9')};
		background: #fff;

		&:focus {
			border: 1.5px solid ${(props) => (props.error ? 'red' : '#acacac')};
		}
	}
`;

const StyledBox = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 8px;
	cursor: pointer;
	color: #1a1d1f;

	& .MuiSvgIcon-root {
		color: #1a1d1f !important;
	}
`;

const NextButton = styled.button({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	cursor: 'pointer',
	width: '67px',
	height: '67px',
	borderRadius: '50%',
	backgroundColor: '#FFBE00',
	boxShadow: '0px 25px 10px 0px rgba(0, 0, 0, 0.07)',
	outline: 'none',
	border: 'none',
	marginTop: '20px',
});

interface OTPAuthProps {
	step: string;
	setStep: (data: string) => void;
	updateStepper: (data: string) => void;
	loginError: string;
	error: string;
	setError: (data: string) => void;
	countryCode: string;
	phoneNumber: string;
	setPhoneNumber: (data: string) => void;
	otp: string[];
	setOtp: (data: string[]) => void;
	loading: boolean;
	setOtpExpiryTime: (data: number) => void;
	handleSubmit: () => void;
	handleOTPSubmit: () => void;
}

const OTPAuth: React.FC<OTPAuthProps> = ({
	step,
	updateStepper,
	loginError,
	error,
	setError,
	countryCode,
	phoneNumber,
	setPhoneNumber,
	otp,
	setOtp,
	loading,
	handleSubmit,
	handleOTPSubmit,
}) => {
	const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);
	const isMobileWidth = IsMobileWidth();

	const handlePhoneNumberChange = (event: string) => {
		setPhoneNumber(event);
		setError('');
	};

	const handleKeyUpChange = (event: React.KeyboardEvent<HTMLDivElement>, index: number) => {
		const target = event.target as HTMLInputElement | HTMLTextAreaElement;
		const value = target.value;

		if (
			(event.code === 'Backspace' || event.keyCode === 8) &&
			index > 0 &&
			index <= 3 &&
			value.length === 0
		) {
			otpInputRefs.current[index - 1]?.focus();
		}
	};

	const handleOtpChange = (
		event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		index: number
	) => {
		const value = event.target.value;
		const numberValidate = /^\d*$/.test(value);
		if (numberValidate && value.length <= 1) {
			const newOtp = [...otp];
			newOtp[index] = value;
			setOtp(newOtp);
			setError('');

			if (value.length === 1 && index < 3 && otpInputRefs.current[index + 1]) {
				otpInputRefs.current[index + 1]?.focus();
			} else if (value.length === 0 && index > 0 && otpInputRefs.current[index - 1]) {
				otpInputRefs.current[index - 1]?.focus();
			}
		} else if (numberValidate && value?.length === 2) {
			//move forward only
			if (otp[index + 1]?.length === 0) {
				otpInputRefs.current[index + 1]?.focus();
			}
		}
	};

	if (isMobileWidth) {
		return (
			<Container>
				{step === '1' ? (
					<FormContainer onSubmit={handleSubmit}>
						<LogoContainer>
							<LogoImage
								style={{ width: '189.6', height: '40px' }}
								src={Logo}
								alt="Logo"
								loading="lazy"
							/>
							{/* <div
								style={{ fontWeight: 700, fontSize: '24px', color: 'var(--default-text-color)' }}
							>
								Buy Spares
							</div> */}
						</LogoContainer>
						<LoginInfoContainer>
							<Heading style={{ fontSize: '20px' }} variant="h2">
								Enter your mobile number
							</Heading>
							<Paragraph style={{ fontSize: '14px' }} variant="body1">
								Please confirm your country code and enter your mobile number
							</Paragraph>
						</LoginInfoContainer>
						<FieldsContainer>
							<PhoneInput
								countryCode={countryCode}
								phoneNumber={phoneNumber}
								setPhoneNumber={setPhoneNumber}
								onChange={(e) => handlePhoneNumberChange(e)}
								error={error}
								setError={setError}
							/>
							{/* <StyledButton
								type="submit"
								disabled={loading || phoneNumber.length < 10}
								variant="contained"
								color="primary"
							>
								{loading ? <CircularProgress size={20} color="customBlack" /> : 'Continue'}
							</StyledButton> */}
							<NextButton
								style={{
									background:
										loading || phoneNumber.length < 10 ? 'rgba(0, 0, 0, 0.12)' : '#FFBE00',
								}}
								type="submit"
								disabled={loading || phoneNumber.length < 10}
							>
								{loading ? (
									<CircularProgress size={20} color="customBlack" />
								) : (
									<img src={NextIcon} alt="next-icon" />
								)}
							</NextButton>
							{error ? (
								<StyledError>
									<img src={ErrorIcon} alt="danger" width="16px" height="16px" />
									<Typography variant="body2">{error}</Typography>
								</StyledError>
							) : null}
							{loginError ? (
								<StyledError>
									<img src={ErrorIcon} alt="danger" width="16px" height="16px" />
									<Typography variant="body2">{loginError}</Typography>
								</StyledError>
							) : null}
						</FieldsContainer>
						<Box display="flex" justifyContent="center" mt={4}>
							<Typography
								variant="body1"
								className="default-lighter-text"
								style={{ fontSize: '14px' }}
							>
								Don't have an account?{' '}
								<Link href="/register" style={{ fontSize: '14px' }}>
									Register
								</Link>
							</Typography>
						</Box>
					</FormContainer>
				) : (
					<div>
						<FormContainer>
							<LogoContainer>
								<LogoImage src={Logo} alt="Logo" loading="lazy" />
								{/* <div
									style={{ fontWeight: 700, fontSize: '24px', color: 'var(--default-text-color)' }}
								>
									Buy Spares
								</div> */}
							</LogoContainer>
							<LoginInfoContainer>
								<Heading style={{ fontSize: '20px' }} variant="h2">
									Enter verification code
								</Heading>
								<Paragraph style={{ fontSize: '14px' }} variant="body1">
									Enter the code we just sent to your number
								</Paragraph>
							</LoginInfoContainer>
							<FieldsContainer>
								<div
									style={{
										display: 'flex',
										justifyContent: 'space-between',
										gap: '12px',
										width: '316px',
									}}
								>
									{Array.from({ length: 4 }).map((_, index) => (
										<StyledTextFieldContainer
											variant="standard"
											key={index}
											size="small"
											type="tel"
											placeholder="-"
											error={error !== ''}
											value={otp[index] || ''}
											onChange={(e) => handleOtpChange(e, index)}
											onKeyUp={(e) => handleKeyUpChange(e, index)}
											InputProps={{
												disableUnderline: true,
												classes: {
													root: 'MuiInputBase-root',
												},
												inputMode: 'numeric',
											}}
											inputRef={(el) => (otpInputRefs.current[index] = el)}
											autoFocus={index === 0}
										/>
									))}
								</div>

								<CustomButton
									type="submit"
									buttonType="round"
									styles={{ width: '233.2px', height: '44px' }}
									title={
										loading ? (
											<CircularProgress size={20} color="customBlack" />
										) : (
											'Verify and Proceed'
										)
									}
									handleClick={handleOTPSubmit}
									disabled={otp.join('').length < 4 || loading}
									color="primary"
								/>
								{error ? (
									<StyledError>
										<img src={ErrorIcon} alt="danger" width="16px" height="16px" />
										<Typography variant="body2">{error}</Typography>
									</StyledError>
								) : null}
								{loginError ? (
									<StyledError>
										<img src={ErrorIcon} alt="danger" width="16px" height="16px" />
										<Typography variant="body2">{loginError}</Typography>
									</StyledError>
								) : null}
							</FieldsContainer>
						</FormContainer>
						<StyledBox onClick={() => updateStepper('1')}>
							<img src={ArrowLeft} alt="arrow-left" width="20px" height="20px" />
							<Typography variant="body1">Back to Previous</Typography>
						</StyledBox>
					</div>
				)}
			</Container>
		);
	}
	return (
		<Container>
			{step === '1' ? (
				<FormContainer onSubmit={handleSubmit}>
					<LogoContainer>
						<LogoImage src={Logo} alt="Logo" loading="lazy" />
						{/* <div style={{ fontWeight: 700, fontSize: '34px', color: 'var(--default-text-color)' }}>
							Buy Spares
						</div> */}
					</LogoContainer>
					<LoginInfoContainer>
						<Heading variant="h2">Enter your mobile number</Heading>
						<Paragraph variant="body1">
							Please confirm your country code and enter your mobile number
						</Paragraph>
					</LoginInfoContainer>
					<FieldsContainer>
						<PhoneInput
							countryCode={countryCode}
							phoneNumber={phoneNumber}
							setPhoneNumber={setPhoneNumber}
							onChange={(e) => handlePhoneNumberChange(e)}
							error={error}
							setError={setError}
						/>
						<StyledButton
							type="submit"
							disabled={loading || phoneNumber.length < 10}
							variant="contained"
							color="primary"
						>
							{loading ? <CircularProgress size={20} color="customBlack" /> : 'Continue'}
						</StyledButton>
						{error ? (
							<StyledError>
								<img src={ErrorIcon} alt="danger" width="16px" height="16px" />
								<Typography variant="body2">{error}</Typography>
							</StyledError>
						) : null}
						{loginError ? (
							<StyledError>
								<img src={ErrorIcon} alt="danger" width="16px" height="16px" />
								<Typography variant="body2">{loginError}</Typography>
							</StyledError>
						) : null}
						<Box display="flex" justifyContent="center" mt={4}>
							<Typography
								variant="body1"
								className="default-lighter-text"
								style={{ fontSize: '14px' }}
							>
								Don't have an account?{' '}
								<Link href="/register" style={{ fontSize: '14px' }}>
									Register
								</Link>
							</Typography>
						</Box>
					</FieldsContainer>
				</FormContainer>
			) : (
				<div>
					<FormContainer>
						<LogoContainer>
							<LogoImage src={Logo} alt="Logo" loading="lazy" />
							{/* <div
								style={{ fontWeight: 700, fontSize: '34px', color: 'var(--default-text-color)' }}
							>
								Buy Spares
							</div> */}
						</LogoContainer>
						<LoginInfoContainer>
							<Heading variant="h2">Enter verification code</Heading>
							<Paragraph variant="body1">Enter the code we just sent to your number</Paragraph>
						</LoginInfoContainer>
						<FieldsContainer>
							<div
								style={{
									display: 'flex',
									justifyContent: 'space-between',
									gap: '12px',
									width: '316px',
								}}
							>
								{Array.from({ length: 4 }).map((_, index) => (
									<StyledTextFieldContainer
										variant="standard"
										key={index}
										size="small"
										type="tel"
										placeholder="-"
										error={error !== ''}
										value={otp[index] || ''}
										onChange={(e) => handleOtpChange(e, index)}
										onKeyUp={(e) => handleKeyUpChange(e, index)}
										InputProps={{
											disableUnderline: true,
											classes: {
												root: 'MuiInputBase-root',
											},
											inputMode: 'numeric',
										}}
										inputRef={(el) => (otpInputRefs.current[index] = el)}
										autoFocus={index === 0}
									/>
								))}
							</div>
							<StyledButton
								type="submit"
								variant="contained"
								disabled={otp.join('').length < 4 || loading}
								color="primary"
								onClick={handleOTPSubmit}
							>
								{loading ? (
									<CircularProgress size={20} color="customBlack" />
								) : (
									'Verify and Proceed'
								)}
							</StyledButton>
							{error ? (
								<StyledError>
									<img src={ErrorIcon} alt="danger" width="16px" height="16px" />
									<Typography variant="body2">{error}</Typography>
								</StyledError>
							) : null}
							{loginError ? (
								<StyledError>
									<img src={ErrorIcon} alt="danger" width="16px" height="16px" />
									<Typography variant="body2">{loginError}</Typography>
								</StyledError>
							) : null}
						</FieldsContainer>
					</FormContainer>
					<StyledBox onClick={() => updateStepper('1')}>
						<img src={ArrowLeft} alt="arrow-left" width="20px" height="20px" />
						<Typography variant="body1">Back to Previous</Typography>
					</StyledBox>
				</div>
			)}
		</Container>
	);
};

export default OTPAuth;
